<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';

const { t } = useI18n();
const { currentAccount, updateAccount, isCloudFeatureEnabled } = useAccount();

const WORKFLOW_DEFS = [
  {
    key: 'archive_drafts_workflow',
    nameKey: 'EVENT_DEFAULT_WORKFLOWS.ARCHIVE_DRAFTS.NAME',
    triggerPrefixKey: 'EVENT_DEFAULT_WORKFLOWS.ARCHIVE_DRAFTS.TRIGGER_PREFIX',
    triggerSuffixKey: 'EVENT_DEFAULT_WORKFLOWS.ARCHIVE_DRAFTS.TRIGGER_SUFFIX',
    actionKey: 'EVENT_DEFAULT_WORKFLOWS.ARCHIVE_DRAFTS.ACTION',
    actionStageKey: 'EVENT_DEFAULT_WORKFLOWS.STAGE.ARCHIVED',
    triggerType: 'time',
    hasRelativeTo: true,
    defaultConfig: {
      active: false,
      relative_to: 'creation_date',
      offset: 30,
      unit: 'days',
    },
  },
  {
    key: 'complete_confirmed_workflow',
    nameKey: 'EVENT_DEFAULT_WORKFLOWS.COMPLETE_CONFIRMED.NAME',
    triggerPrefixKey:
      'EVENT_DEFAULT_WORKFLOWS.COMPLETE_CONFIRMED.TRIGGER_PREFIX',
    triggerSuffixKey:
      'EVENT_DEFAULT_WORKFLOWS.COMPLETE_CONFIRMED.TRIGGER_SUFFIX',
    actionKey: 'EVENT_DEFAULT_WORKFLOWS.COMPLETE_CONFIRMED.ACTION',
    actionStageKey: 'EVENT_DEFAULT_WORKFLOWS.STAGE.COMPLETED',
    hasRelativeTo: true,
    defaultConfig: {
      active: false,
      relative_to: 'end_date',
      offset: 1,
      unit: 'days',
    },
  },
  {
    key: 'archive_completed_workflow',
    nameKey: 'EVENT_DEFAULT_WORKFLOWS.ARCHIVE_COMPLETED.NAME',
    triggerPrefixKey:
      'EVENT_DEFAULT_WORKFLOWS.ARCHIVE_COMPLETED.TRIGGER_PREFIX',
    triggerSuffixKey:
      'EVENT_DEFAULT_WORKFLOWS.ARCHIVE_COMPLETED.TRIGGER_SUFFIX',
    actionKey: 'EVENT_DEFAULT_WORKFLOWS.ARCHIVE_COMPLETED.ACTION',
    actionStageKey: 'EVENT_DEFAULT_WORKFLOWS.STAGE.ARCHIVED',
    triggerType: 'time',
    hasRelativeTo: true,
    defaultConfig: {
      active: false,
      relative_to: 'end_date',
      offset: 7,
      unit: 'days',
    },
  },
  {
    key: 'inquiry_closed_lost_workflow',
    nameKey: 'EVENT_DEFAULT_WORKFLOWS.INQUIRY_CLOSED_LOST.NAME',
    triggerPrefixKey:
      'EVENT_DEFAULT_WORKFLOWS.INQUIRY_CLOSED_LOST.TRIGGER_PREFIX',
    triggerSuffixKey: null,
    actionKey: 'EVENT_DEFAULT_WORKFLOWS.INQUIRY_CLOSED_LOST.ACTION',
    actionStageKey: 'EVENT_DEFAULT_WORKFLOWS.STAGE.LOST',
    triggerType: 'time',
    hasRelativeTo: false,
    defaultConfig: { active: false, offset: 14, unit: 'days' },
  },
  {
    key: 'quote_accepted_stage_workflow',
    nameKey: 'EVENT_DEFAULT_WORKFLOWS.QUOTE_ACCEPTED.NAME',
    triggerKey: 'EVENT_DEFAULT_WORKFLOWS.QUOTE_ACCEPTED.TRIGGER',
    actionKey: 'EVENT_DEFAULT_WORKFLOWS.QUOTE_ACCEPTED.ACTION',
    triggerType: 'event',
    configurableStage: true,
    defaultConfig: { active: false, stage: 'confirmed' },
  },
];

const SYSTEM_STAGES = [
  'draft',
  'inquiry',
  'confirmed',
  'completed',
  'lost',
  'archived',
];

const unitOptions = computed(() => [
  {
    value: 'minutes',
    label: t('EVENT_DEFAULT_WORKFLOWS.FORM.UNIT_OPTIONS.MINUTES'),
  },
  {
    value: 'hours',
    label: t('EVENT_DEFAULT_WORKFLOWS.FORM.UNIT_OPTIONS.HOURS'),
  },
  { value: 'days', label: t('EVENT_DEFAULT_WORKFLOWS.FORM.UNIT_OPTIONS.DAYS') },
  {
    value: 'weeks',
    label: t('EVENT_DEFAULT_WORKFLOWS.FORM.UNIT_OPTIONS.WEEKS'),
  },
  {
    value: 'months',
    label: t('EVENT_DEFAULT_WORKFLOWS.FORM.UNIT_OPTIONS.MONTHS'),
  },
]);

const relativeToOptions = computed(() => [
  {
    value: 'creation_date',
    label: t('EVENT_DEFAULT_WORKFLOWS.FORM.RELATIVE_TO_OPTIONS.CREATION_DATE'),
  },
  {
    value: 'start_date',
    label: t('EVENT_DEFAULT_WORKFLOWS.FORM.RELATIVE_TO_OPTIONS.START_DATE'),
  },
  {
    value: 'end_date',
    label: t('EVENT_DEFAULT_WORKFLOWS.FORM.RELATIVE_TO_OPTIONS.END_DATE'),
  },
]);

const stageOptions = computed(() =>
  SYSTEM_STAGES.map(stage => ({
    value: stage,
    label: t(`EVENT_DEFAULT_WORKFLOWS.STAGE.${stage.toUpperCase()}`),
  }))
);

const workflowDefs = computed(() =>
  WORKFLOW_DEFS.filter(
    def =>
      def.key !== 'quote_accepted_stage_workflow' ||
      isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING_QUOTES)
  )
);

const selectedKey = ref(workflowDefs.value[0].key);
const isSaving = ref(false);

const selectedDef = computed(() =>
  workflowDefs.value.find(d => d.key === selectedKey.value)
);

const getConfig = key => {
  const def = workflowDefs.value.find(d => d.key === key);
  if (!def) return { active: false };

  const stored = currentAccount.value?.settings?.[key];
  return { ...def.defaultConfig, ...(stored || {}) };
};

const localConfig = ref(getConfig(selectedKey.value));

const selectWorkflow = key => {
  selectedKey.value = key;
  localConfig.value = getConfig(key);
};

const isActive = key => {
  const config = currentAccount.value?.settings?.[key];
  return config ? config.active : false;
};

const toggleActive = async () => {
  const newActive = !localConfig.value.active;
  localConfig.value = { ...localConfig.value, active: newActive };
  await save(true);
};

const save = async (silent = false) => {
  isSaving.value = true;
  try {
    await updateAccount(
      { [selectedKey.value]: { ...localConfig.value } },
      { silent: true }
    );
    if (!silent) useAlert(t('EVENT_DEFAULT_WORKFLOWS.SAVE.SUCCESS'));
  } catch {
    useAlert(t('EVENT_DEFAULT_WORKFLOWS.SAVE.ERROR'));
    localConfig.value = getConfig(selectedKey.value);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="outline outline-1 outline-n-container rounded-xl bg-n-solid-2 overflow-hidden"
  >
    <!-- Header -->
    <div class="px-5 py-4 border-b border-n-container">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('EVENT_DEFAULT_WORKFLOWS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ $t('EVENT_DEFAULT_WORKFLOWS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Two-panel layout -->
    <div class="flex divide-x divide-n-container">
      <!-- Left sidebar: workflow list -->
      <div class="w-64 shrink-0 py-2">
        <button
          v-for="def in workflowDefs"
          :key="def.key"
          type="button"
          class="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
          :class="
            selectedKey === def.key
              ? 'bg-n-alpha-black2 text-n-slate-12'
              : 'text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12'
          "
          @click="selectWorkflow(def.key)"
        >
          <!-- Status dot -->
          <span
            class="w-2 h-2 rounded-full shrink-0"
            :class="isActive(def.key) ? 'bg-n-brand' : 'bg-n-slate-6'"
          />
          <span class="text-sm font-medium truncate">{{
            $t(def.nameKey)
          }}</span>
        </button>
      </div>

      <!-- Right panel: detail view -->
      <div v-if="selectedDef" class="flex-1 p-6 space-y-0">
        <!-- Workflow header row -->
        <div class="flex items-center justify-between mb-6">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ $t(selectedDef.nameKey) }}
          </h4>
          <div class="flex items-center gap-3">
            <Button
              :label="$t('EVENT_DEFAULT_WORKFLOWS.SAVE.BUTTON')"
              size="sm"
              :is-loading="isSaving"
              @click="save()"
            />
            <!-- Toggle -->
            <Switch :model-value="localConfig.active" @change="toggleActive" />
          </div>
        </div>

        <!-- Trigger block -->
        <div
          class="rounded-lg border border-n-container bg-n-solid-1 px-4 py-3"
        >
          <div class="flex items-center gap-2 mb-2">
            <span
              class="w-4 h-4 text-n-slate-10 shrink-0"
              :class="
                selectedDef.triggerType === 'event'
                  ? 'i-lucide-zap'
                  : 'i-lucide-clock'
              "
            />
            <span
              class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ $t('EVENT_DEFAULT_WORKFLOWS.TRIGGER_LABEL') }}
            </span>
          </div>
          <div
            v-if="selectedDef.triggerType === 'event'"
            class="flex items-center gap-2 flex-wrap"
          >
            <span class="text-sm text-n-slate-12">{{
              $t(selectedDef.triggerKey)
            }}</span>
          </div>
          <div v-else class="flex items-center gap-2 flex-wrap">
            <span class="text-sm text-n-slate-12">{{
              $t(selectedDef.triggerPrefixKey)
            }}</span>
            <Input
              v-model="localConfig.offset"
              type="number"
              min="1"
              class="w-20 [&>input]:h-8 [&>input]:py-1.5 [&>input]:outline-offset-0"
            />
            <FilterSelect
              v-model="localConfig.unit"
              variant="faded"
              hide-icon
              :options="unitOptions"
            />
            <template v-if="selectedDef.hasRelativeTo">
              <span class="text-sm text-n-slate-12">{{
                $t(selectedDef.triggerSuffixKey)
              }}</span>
              <FilterSelect
                v-model="localConfig.relative_to"
                variant="faded"
                hide-icon
                :options="relativeToOptions"
              />
            </template>
          </div>
        </div>

        <!-- Connector line -->
        <div class="flex flex-col items-center py-1">
          <div class="w-px h-3 bg-n-container" />
          <div class="w-2 h-2 rounded-full bg-n-container" />
          <div class="w-px h-3 bg-n-container" />
        </div>

        <!-- Action block -->
        <div
          class="rounded-lg border border-n-container bg-n-solid-1 px-4 py-3"
        >
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-zap w-4 h-4 text-n-slate-10 shrink-0" />
            <span
              class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ $t('EVENT_DEFAULT_WORKFLOWS.ACTION_LABEL') }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm text-n-slate-12">{{
              $t(selectedDef.actionKey)
            }}</span>
            <FilterSelect
              v-if="selectedDef.configurableStage"
              v-model="localConfig.stage"
              variant="faded"
              hide-icon
              :options="stageOptions"
            />
            <span
              v-else
              class="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium bg-n-alpha-2 text-n-slate-11 border border-n-weak cursor-default select-none"
            >
              {{ $t(selectedDef.actionStageKey) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
